import type {StreamChat} from 'stream-chat'

export type ChatConnectionStatus = 'off' | 'connecting' | 'connected' | 'session-expired' | 'error'

export interface ChatConnectionSnapshot {
    status: ChatConnectionStatus
    /** The connected client, for the chat UI to hand to the SDK's `Chat` component. */
    client: StreamChat | null
    /** The Duesy organization the connection is for. */
    organizationId: string | null
    /** The caller's chat user id (`user-{id}`). */
    userId: string | null
}

type Listener = () => void

/** A connected snapshot, with its client, organization and chat user. */
export type LiveChatConnection = ChatConnectionSnapshot & {status: 'connected', client: StreamChat, organizationId: string, userId: string}

export const isLiveChatConnection = (snapshot: ChatConnectionSnapshot): snapshot is LiveChatConnection =>
    snapshot.status === 'connected' && snapshot.client !== null && snapshot.organizationId !== null && snapshot.userId !== null

export const chatOff: ChatConnectionSnapshot = {status: 'off', client: null, organizationId: null, userId: null}

let snapshot: ChatConnectionSnapshot = chatOff
const listeners = new Set<Listener>()
let connection: Promise<typeof import('./chatConnection')> | null = null

export const subscribeChatConnection = (listener: Listener): (() => void) => {
    listeners.add(listener)

    return () => {
        listeners.delete(listener)
    }
}

export const getChatConnectionSnapshot = (): ChatConnectionSnapshot => snapshot

/** Written by the connection module; the snapshot keeps its identity when nothing changed. */
export const publishChatConnection = (next: ChatConnectionSnapshot): void => {
    if (next.status === snapshot.status && next.client === snapshot.client
        && next.organizationId === snapshot.organizationId && next.userId === snapshot.userId) {
        return
    }

    snapshot = next

    for (const listener of listeners) {
        listener()
    }
}

/**
 * What the layout wants: a connection for `organizationId`, or none. The SDK and its CSS load on the first request
 * for an organization, so pages without chat never fetch them; a `null` request only reaches the module when it
 * already loaded, since nothing needs disconnecting otherwise. A chunk that fails to load also fires Vite's
 * `vite:preloadError`, so `installChunkReloadGuard` reloads the page once as for any chunk; the `error` snapshot
 * and the retry below cover the second failure the guard suppresses.
 */
export const requestChatConnection = (organizationId: string | null): void => {
    if (organizationId === null) {
        if (connection === null) {
            publishChatConnection(chatOff)

            return
        }

        void connection.then(module => module.disconnectChat(), () => {})

        return
    }

    connection ??= import('./chatConnection').catch((error: unknown) => {
        // A chunk that failed to load (offline, say) is tried again on the next request.
        connection = null
        console.error('Chat failed to load', error)
        publishChatConnection({...chatOff, status: 'error', organizationId})

        throw error
    })

    void connection.then(module => module.connectChat(organizationId), () => {})
}
